import React, { useState } from 'react';
import {
  Card, Button, Input, InputNumber, Select, TimePicker, Space,
  List, Tag, Alert, Divider, Popconfirm, Empty, Checkbox, message
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ClockCircleOutlined,
  TeamOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { DIAS_OPTIONS } from '../constants';
import { sortDias } from '../utils';
import { InfinityIcon } from 'lucide-react';
import { useNotify } from '../../../../../../providers/NotificationProvider';

export default function Paso4Horarios({ tallerId, onHorariosChange, loading }) {
  const [horarios, setHorarios] = useState([]);
  const [bloques, setBloques] = useState([]);

  // Estados para el formulario temporal
  const [nombreHorario, setNombreHorario] = useState('');
  const [cuposDisponibles, setCuposDisponibles] = useState(null);
  const [sku,setSku] = useState(null)
  const [cuposIlimitados, setCuposIlimitados] = useState(false);
  const [diasSeleccionados, setDiasSeleccionados] = useState([]);
  const [horaInicio, setHoraInicio] = useState(null);
  const [horaFin, setHoraFin] = useState(null);
  const notify = useNotify(); 

  const handleAddBloque = () => {
    if (!diasSeleccionados || diasSeleccionados.length === 0 || !horaInicio || !horaFin) {
      notify.error('Por favor completa todos los campos del bloque (días, hora inicio y hora fin)');
      return;
    }

    if (horaFin.isBefore(horaInicio)) {
      notify.error('La hora de fin no puede ser anterior a la hora de inicio');
      return;
    }

    const nuevoBloque = {
      id: Date.now(),
      dias: sortDias(diasSeleccionados),
      inicio: horaInicio.format('HH:mm'),
      fin: horaFin.format('HH:mm')
    };

    setBloques([...bloques, nuevoBloque]);

    setDiasSeleccionados([]);
    setHoraInicio(null);
    setHoraFin(null);
  };


  const handleRemoveBloque = (bloqueId) => {
    setBloques(bloques.filter(b => b.id !== bloqueId));
  };


  const handleCrearHorario = () => {
    // Validar
    if (!nombreHorario || bloques.length === 0) {
      return;
    }

    if (!cuposIlimitados && (!cuposDisponibles || cuposDisponibles <= 0)) {
      return;
    }

    const nuevoHorario = {
      id: Date.now(),
      nombre: nombreHorario,
      cuposDisponibles: cuposIlimitados ? 0 : cuposDisponibles,
      sku:sku,
      cuposIlimitados: cuposIlimitados,
      bloques: bloques.map(b => ({
        dias: b.dias,
        inicio: b.inicio,
        fin: b.fin
      }))
    };

    const nuevosHorarios = [...horarios, nuevoHorario];
    setHorarios(nuevosHorarios);

    // Limpiar todo
    setBloques([]);
    setNombreHorario('');
    setSku(null)
    setCuposDisponibles(null);
    setCuposIlimitados(false);

    if (onHorariosChange) {
      onHorariosChange(nuevosHorarios);
    }
  };


  const handleEliminarHorario = (horarioId) => {
    const nuevosHorarios = horarios.filter(h => h.id !== horarioId);
    setHorarios(nuevosHorarios);

    if (onHorariosChange) {
      onHorariosChange(nuevosHorarios);
    }
  };


  return (
    <div className="space-y-6">
      <Alert
        message="Configuración de horarios"
        description="Crea horarios con uno o más bloques de días y horas. Puedes agregar múltiples horarios o saltar este paso y agregarlos después."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
      />

      {/* Formulario de horario */}
      <Card title="➕ Crear nuevo horario">
        <div className="space-y-4">
          {/* Nombre del horario */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Nombre del horario <span className="text-red-500">*</span>
            </label>
            <Input
              size="large"
              placeholder="Ej: Mañana, Tarde, Grupo A"
              value={nombreHorario}
              onChange={(e) => setNombreHorario(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Codigo en bsale
            </label>
            <Input
              size="large"
              placeholder="Codigo otorgado por Bsale"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </div>

          {/* Cupos disponibles */}
          <div>
            <div className="mb-3">
              <Checkbox
                checked={cuposIlimitados}
                onChange={(e) => {
                  setCuposIlimitados(e.target.checked);
                  if (e.target.checked) {
                    setCuposDisponibles(null);
                  }
                }}
              >
                <Space>
                  <InfinityIcon />
                  Cupos ilimitados
                </Space>
              </Checkbox>
            </div>

            {!cuposIlimitados && (
              <>
                <label className="block text-sm font-medium mb-2">
                  Cupos disponibles <span className="text-red-500">*</span>
                </label>
                <InputNumber
                  size="large"
                  min={1}
                  className="w-full"
                  placeholder="Cantidad de cupos"
                  prefix={<TeamOutlined />}
                  value={cuposDisponibles}
                  onChange={setCuposDisponibles}
                />
              </>
            )}

            {cuposIlimitados && (
              <div className="p-3 bg-blue-50 rounded text-sm text-blue-700">
                <InfinityIcon className="mr-2" />
                Este horario no tiene límite de cupos
              </div>
            )}
          </div>

          <Divider>Bloques de horario</Divider>

          {/* Agregar bloques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-2">Días</label>
              <Select
                mode="multiple"
                options={DIAS_OPTIONS}
                placeholder="Selecciona días"
                className="w-full"
                value={diasSeleccionados}
                onChange={setDiasSeleccionados}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Hora inicio</label>
              <TimePicker
                format="HH:mm"
                className="w-full"
                placeholder="00:00"
                showNow={false}
                value={horaInicio}
                onChange={setHoraInicio}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Hora fin</label>
              <TimePicker
                format="HH:mm"
                className="w-full"
                placeholder="00:00"
                showNow={false}
                value={horaFin}
                onChange={setHoraFin}
              />
            </div>
          </div>

          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={handleAddBloque}
            disabled={!diasSeleccionados.length || !horaInicio || !horaFin}
          >
            Agregar bloque
          </Button>

          {/* Lista de bloques temporales */}
          {bloques.length > 0 && (
            <Card size="small" title="Bloques agregados" className="bg-gray-50">
              <List
                size="small"
                dataSource={bloques}
                renderItem={(bloque) => (
                  <List.Item
                    actions={[
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveBloque(bloque.id)}
                      />
                    ]}
                  >
                    <Space wrap>
                      {bloque.dias.map(dia => (
                        <Tag key={dia} color="blue" className="capitalize">
                          {dia}
                        </Tag>
                      ))}
                      <Tag color="blue" icon={<ClockCircleOutlined />}>
                        {bloque.inicio} - {bloque.fin}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          )}

          <Button
            type="primary"
            size="large"
            block
            icon={<PlusOutlined />}
            onClick={handleCrearHorario}
            disabled={
              !nombreHorario ||
              bloques.length === 0 ||
              (!cuposIlimitados && (!cuposDisponibles || cuposDisponibles <= 0))
            }
            loading={loading}
          >
            Crear horario
          </Button>
        </div>
      </Card>

      {/* Lista de horarios creados */}
      {horarios.length > 0 ? (
        <Card title={`📋 Horarios creados (${horarios.length})`}>
          <List
            dataSource={horarios}
            renderItem={(horario) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="¿Eliminar horario?"
                    description="Esta acción no se puede deshacer"
                    onConfirm={() => handleEliminarHorario(horario.id)}
                    okText="Eliminar"
                    cancelText="Cancelar"
                    okButtonProps={{ danger: true }}
                  >
                    <Button danger size="small" icon={<DeleteOutlined />}>
                      Eliminar
                    </Button>
                  </Popconfirm>
                ]}
              >
                <div className="w-full">
                  <div className="font-semibold mb-2 flex items-center gap-2">
                    {horario.nombre}
                    {horario.cuposIlimitados ? (
                      <Tag className='flex gap-2' color="blue" icon={<InfinityIcon />}>
                        Cupos ilimitados
                      </Tag>
                    ) : (
                      <Tag color="orange">
                        {horario.cuposDisponibles} cupos
                      </Tag>
                    )}
                  </div>
                  {horario.bloques.map((bloque, idx) => (
                    <div key={idx} className="mb-1">
                      <Space wrap size="small">
                        {bloque.dias.map(dia => (
                          <Tag key={dia} color="blue" size="small" className="capitalize">
                            {dia}
                          </Tag>
                        ))}
                        <Tag color="blue" size="small" icon={<ClockCircleOutlined />}>
                          {bloque.inicio} - {bloque.fin}
                        </Tag>
                      </Space>
                    </div>
                  ))}
                </div>
              </List.Item>
            )}
          />
        </Card>
      ) : (
        <Empty
          description="No hay horarios creados aún"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <p className="text-sm text-gray-500 mt-2">
            Puedes agregar horarios ahora o saltar este paso y agregarlos más tarde
          </p>
        </Empty>
      )}
    </div>
  );
}
